/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Link,
  Stack,
  IconButton,
  Divider,
} from '@mui/material';
import { ZoomIn, ZoomOut, ZoomOutMap } from '@mui/icons-material';
import { Button } from '@/components/button';
import Typography from '@/components/typography/typography';
import { Edit, Eye, EyeOff } from '@/components/icons';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { templateRoutes } from '../utils/common';
import { EnhancedSectionData } from '../types/editor';
import './EditorHeader.scss';


interface EditorHeaderProps {
  onSave: () => void;
  onPreview: () => void;
  preview: boolean;
  editHtml: boolean;
  onEditHtml: () => void;
  onEditModeChange?: () => void;
  sections: EnhancedSectionData[];
  isEditingHeaderFooter: boolean;
  setIsEditingHeaderFooter: (value: boolean) => void;
  onSaveHeaderFooter: (value: boolean) => void;
  showSample?: boolean;
  setShowSample?: (value: boolean | ((prev: boolean) => boolean)) => void;
  sampleType?: 'quotation' | 'invoice' | 'delivery';
  setSampleType?: (value: 'quotation' | 'invoice' | 'delivery') => void;
  samplePages?: number;
  setSamplePages?: (value: number | ((prev: number) => number)) => void;
  modulePath: string;
  type: string;
  mode: 'add' | 'edit';
  templateData: Record<string, any> | null;
  isSaving?: boolean;
  editMode: boolean;
  fromPreview?: boolean;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({
  onSave,
  onPreview,
  preview,
  editHtml,
  onEditHtml,
  sections,
  isEditingHeaderFooter,
  onEditModeChange,
  setIsEditingHeaderFooter,
  onSaveHeaderFooter,
  showSample,
  setShowSample,
  sampleType,
  setSampleType,
  samplePages,
  setSamplePages,
  modulePath,
  type,
  mode = 'add',
  templateData,
  isSaving = false,
  editMode,
  fromPreview = false,
  zoom,
  onZoomIn,
  onZoomOut,
  onResetZoom,
}) => {
  const ROUTES = templateRoutes(modulePath, type);
  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      sx={{
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Toolbar variant="dense" sx={{ justifyContent: 'space-between', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Link
            component={RouterLink}
            to={ROUTES.LIST}
            underline="hover"
            color="text.secondary"
            sx={{ fontSize: '0.875rem' }}
          >
            <Typography type="s5" color='theme.secondary.800'>{type === 'email' ? 'Emails' : 'PDF'}</Typography>
          </Link>
          <Typography type="s5" weight='medium' color='theme.secondary.1000'>/</Typography>
          <Typography type="s5" weight='medium' color='theme.secondary.1000'>
            {mode === 'add' ? 'Add New' : editMode ? 'Edit' : 'Preview'} Template
          </Typography>
          {templateData && templateData?.name &&
            <>
              <Typography type="s5" weight='medium' color='theme.secondary.1000'>/</Typography>
              <Typography type="s5" weight='medium' color='theme.secondary.1000'>
                {templateData?.name}
              </Typography>
            </>
          }
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" className="editorHeader--Buttons">
          {/* Zoom Controls - Only show in preview mode */}
          {preview && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: 1,
                  bgcolor: 'rgba(74, 192, 140, 0.08)',
                  border: '1px solid rgba(74, 192, 140, 0.2)',
                }}
              >
                <IconButton
                  size="small"
                  onClick={onZoomOut}
                  disabled={zoom <= 50}
                  color='#4AC08C'
                  sx={{
                    color: '#4AC08C',
                    '&:hover': {
                      bgcolor: 'rgba(74, 192, 140, 0.15)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.3,
                      color: '#4AC08C'
                    }
                  }}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>

                <Typography
                  type="s5"
                  weight='medium'
                  sx={{
                    minWidth: '50px',
                    textAlign: 'center',
                    color: '#4AC08C'
                  }}
                >
                  {zoom}%
                </Typography>

                <IconButton
                  size="small"
                  onClick={onZoomIn}
                  disabled={zoom >= 150}
                  sx={{
                    color: '#4AC08C',
                    '&:hover': {
                      bgcolor: 'rgba(74, 192, 140, 0.15)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.3,
                      color: '#4AC08C'
                    }
                  }}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    mx: 0.5,
                    bgcolor: 'rgba(74, 192, 140, 0.3)'
                  }}
                />

                <IconButton
                  size="small"
                  onClick={onResetZoom}
                  disabled={zoom === 100}
                  title="Reset Zoom"
                  sx={{
                    color: '#4AC08C',
                    '&:hover': {
                      bgcolor: 'rgba(74, 192, 140, 0.15)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.3,
                      color: '#4AC08C'
                    }
                  }}
                >
                  <ZoomOutMap fontSize="small" />
                </IconButton>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </>
          )}

          {!fromPreview ? (
            <>
              <button
                onClick={() => setIsEditingHeaderFooter(true)}
                className="pbtn"
                disabled={preview}
              >
                <Edit fontSize='small' />
                Edit Header/Footer
              </button>
              
              <div className="pb-lens">
                <button 
                  className={!preview ? 'is-active' : ''} 
                  onClick={() => preview && onPreview()}
                  disabled={!sections?.length}
                >
                  <Edit fontSize="small" /> Edit
                </button>
                <button 
                  className={preview ? 'is-active' : ''} 
                  onClick={() => !preview && onPreview()}
                  disabled={!sections?.length}
                >
                  <Eye fontSize="small" /> Preview
                </button>
              </div>
              <button
                onClick={onEditHtml}
                className={`pbtn ${editHtml ? 'pbtn--primary' : ''}`}
                disabled={!sections?.length || preview}
              >
                <CodeOutlinedIcon fontSize='small' />
                {editHtml ? 'Exit HTML Edit' : 'Edit HTML'}
              </button>
              
              {!isSaving && (
                <span className="pb-bar-stat is-dirty">
                  <span className="pb-bar-dirty">
                    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" fill="currentColor"/></svg>
                  </span>
                  Unsaved
                </span>
              )}
              <button
                onClick={onSave}
                className="pbtn pbtn--primary"
                disabled={!sections?.length || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            editMode ? (
              <>
                {isEditingHeaderFooter ? (
                  <>
                    {setShowSample && (
                      <>
                        <button 
                          className="st-sample-tg" 
                          onClick={() => setShowSample(v => !v)}
                          title="Preview the master wrapped around a real document"
                        >
                          <span className={`cx-switch ${showSample ? 'is-on' : ''}`} aria-hidden>
                            <span className="cx-switch-dot" />
                          </span>
                          Sample content
                        </button>
                        {showSample && (
                          <div className="st-docchips">
                            {(['quotation', 'invoice', 'delivery'] as const).map(d => (
                              <button key={d} className={`st-docchip ${sampleType === d ? 'is-active' : ''}`} onClick={() => setSampleType && setSampleType(d)}>
                                <span className="st-docchip-dot" style={{ background: d === 'quotation' ? '#1A8A56' : d === 'invoice' ? '#1B2C6E' : '#8a540b' }} />
                                {d === 'quotation' && <DescriptionOutlinedIcon sx={{ width: 12, height: 12 }} />}
                                {d === 'invoice' && <ReceiptOutlinedIcon sx={{ width: 12, height: 12 }} />}
                                {d === 'delivery' && <LocalShippingOutlinedIcon sx={{ width: 12, height: 12 }} />}
                                {d === 'quotation' ? 'Quotation' : d === 'invoice' ? 'Tax Invoice' : 'Delivery Note'}
                              </button>
                            ))}
                            <button className={`st-docchip ${samplePages === 2 ? 'is-active' : ''}`} onClick={() => setSamplePages && setSamplePages(samplePages === 1 ? 2 : 1)} title="Add a continuation page to test header/footer repeat">
                              <FileCopyOutlinedIcon sx={{ width: 12, height: 12 }} />
                              {samplePages === 2 ? '2 pages' : '1 page'}
                            </button>
                          </div>
                        )}
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      </>
                    )}
                    <button
                      onClick={() => setIsEditingHeaderFooter(false)}
                      className="pbtn pbtn--ghost"
                    >
                      Cancel Editing
                    </button>
                    <button
                      onClick={() => onSaveHeaderFooter(true)}
                      className="pbtn pbtn--primary"
                    >
                      Save Changes
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setIsEditingHeaderFooter(true)}
                      className="pbtn"
                      disabled={preview}
                    >
                      <Edit fontSize='small' />
                      Edit Header/Footer
                    </button>
                    
                    <div className="pb-lens">
                      <button 
                        className={!preview ? 'is-active' : ''} 
                        onClick={() => preview && onPreview()}
                        disabled={!sections?.length}
                      >
                        <Edit fontSize="small" /> Edit
                      </button>
                      <button 
                        className={preview ? 'is-active' : ''} 
                        onClick={() => !preview && onPreview()}
                        disabled={!sections?.length}
                      >
                        <Eye fontSize="small" /> Preview
                      </button>
                    </div>

                    {!isSaving && (
                      <span className="pb-bar-stat is-dirty">
                        <span className="pb-bar-dirty">
                          <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" fill="currentColor"/></svg>
                        </span>
                        Unsaved
                      </span>
                    )}
                    <button
                      onClick={onSave}
                      className="pbtn pbtn--primary"
                      disabled={!sections?.length}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => onEditModeChange && onEditModeChange()}
                      className="pbtn pbtn--ghost"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </>
            ) : (
              <button
                onClick={() => onEditModeChange && onEditModeChange()}
                className="pbtn pbtn--primary"
                disabled={!sections?.length}
              >
                <Edit fontSize='small' />
                Edit
              </button>
            )
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default EditorHeader;